import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import {bffApi} from "../chat/bffApi";

export type NewsFilterKey = "groupings" | "sources";

/**
 * News filter state, kept in the URL as search params
 * (?groupings=skatt&sources=DIB) so the sidebar (in DashboardLayout) and the
 * page (in the Outlet) share it without prop plumbing, and filtered views are
 * deep-linkable. `groupings` holds a portal-grouping slug (see
 * useNewsGroupings). Both facets are single-select; params are still read as
 * repeated values so older multi-select links resolve to their first entry.
 */
export function useNewsFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [trackFiltered] = bffApi.endpoints.trackFiltered.useMutation();

    const grouping = searchParams.get("groupings");
    const source = searchParams.get("sources");

    const setValue = useCallback(
        (key: NewsFilterKey, value: string | null) => {
            // Single choke point for every news-filter surface (sidebar,
            // mobile select, source pills). Clearing counts as "<key>:all".
            trackFiltered({
                entity: "news",
                filter: `${key}:${value ?? "all"}`,
            });
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete(key);
                    if (value !== null) next.append(key, value);
                    return next;
                },
                {replace: true}
            );
        },
        [setSearchParams, trackFiltered]
    );

    // Clicking the selected value clears the filter, so a source pill or
    // sidebar row doubles as its own "off" switch.
    const toggleValue = useCallback(
        (key: NewsFilterKey, value: string) => {
            setValue(key, searchParams.get(key) === value ? null : value);
        },
        [setValue, searchParams]
    );

    return useMemo(
        () => ({grouping, source, setValue, toggleValue}),
        [grouping, source, setValue, toggleValue]
    );
}
